import SvgIcon, {SvgIconProps} from '@mui/material/SvgIcon';
import {BaseNavIcon} from './BaseNavIcon';

/** Brand mark — parent sets color via `sx` (e.g. `primary.main` in the sidebar header). */
export function LogoIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24" sx={{color: 'inherit', fontSize: 28, ...props.sx}}>
      <path
        fill="currentColor"
        d="M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3zm5 15h-2v-6H9v6H7v-7.8l5-4.5 5 4.5V18z"
      />
    </SvgIcon>
  );
}

export function DashboardIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path fill="currentColor" d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z" />
    </BaseNavIcon>
  );
}

/** Properties — building / units (original art). */
export function PropertyIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M12 3L2 12h3v8h14v-8h3L12 3zm0 15.5h-5v-3.5H5v-2.5l7-6.2 7 6.2v2.5h-2v3.5H12zM9 10h2v2H9v-2zm4 0h2v2h-2v-2zM9 14h2v2H9v-2zm4 0h2v2h-2v-2z"
      />
    </BaseNavIcon>
  );
}

/** Residents — community (original art). */
export function TenantIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M12 5.5A3.5 3.5 0 0 1 15.5 9a3.5 3.5 0 0 1-3.5 3.5A3.5 3.5 0 0 1 8.5 9a3.5 3.5 0 0 1 3.5-3.5zM5 8c.56 0 1.08.15 1.53.42-.15 1.43.27 2.85 1.13 3.96C7.16 13.34 6.16 14 5 14a3 3 0 0 1-3-3 3 3 0 0 1 3-3zm14 0a3 3 0 0 1 3 3 3 3 0 0 1-3 3c-1.16 0-2.16-.66-2.66-1.62a5.536 5.536 0 0 0 1.13-3.96c.45-.27.97-.42 1.53-.42zM5.5 18.25c0-2.07 2.91-3.75 6.5-3.75s6.5 1.68 6.5 3.75V20h-13v-1.75zM0 20v-1.5c0-1.39 1.89-2.56 4.45-2.9-.59.68-.95 1.62-.95 2.65V20H0zm24 0h-3.5v-1.75c0-1.03-.36-1.97-.95-2.65 2.56.34 4.45 1.51 4.45 2.9V20z"
      />
    </BaseNavIcon>
  );
}

/** Devices & Access — original phone / keypad device artwork. */
export function DevicesIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M8,3C6.89,3 6,3.89 6,5V19C6,20.11 6.89,21 8,21H16C17.11,21 18,20.11 18,19V5C18,3.89 17.11,3 16,3H8M8,5H16V19H8V5M13,8V10H15V8H13M9,8H11V10H9V8M13,11V13H15V11H13M9,11H11V13H9V11M9,14H15V16H9V14Z"
      />
    </BaseNavIcon>
  );
}

/** Deliveries — delivery truck (original art). */
export function DeliverIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M3 4C1.9 4 1 4.9 1 6v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-4.67L20.24 6H17V4H3zm11 2h2.5l2.75 5H14V6zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"
      />
    </BaseNavIcon>
  );
}

/** Activity — clock / history (logs & investigation). */
export function EventSearchIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6.2l4.6 2.8.8-1.3-4.1-2.5V7z"
      />
    </BaseNavIcon>
  );
}

export const ActivityIcon = EventSearchIcon;

/**
 * Portal Users — org / app operator (profile + account lines), not HOA residents.
 */
export function CommunityIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <circle cx="8.5" cy="9.5" r="2.35" fill="currentColor" />
      <path
        fill="currentColor"
        d="M5 17.5v-1.2c0-1.4 1.6-2.3 3.5-2.3s3.5.9 3.5 2.3v1.2H5z"
      />
      <path
        fill="currentColor"
        d="M13 6.5h7v1.4h-7V6.5zm0 3.2h7v1.4h-7V9.7zm0 3.2h5.5v1.4H13v-1.4z"
      />
    </BaseNavIcon>
  );
}

/** Integration — third-party / external systems (hub topology). */
export function IntegrationIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <circle cx="12" cy="6.5" r="2.25" fill="currentColor" />
      <circle cx="6.5" cy="16.5" r="2.25" fill="currentColor" />
      <circle cx="17.5" cy="16.5" r="2.25" fill="currentColor" />
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.65"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 8.7v2.9l-4.1 2.5M12 11.6l4.1 2.5"
      />
    </BaseNavIcon>
  );
}

/** License — shield + check (entitlement). */
export function LicenseIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3zm-1.06 13.54L7.4 12l1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41-5.66 5.66z"
      />
    </BaseNavIcon>
  );
}

export function SettingsIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"
      />
    </BaseNavIcon>
  );
}

export function VisitorIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M12 11a3 3 0 100-6 3 3 0 000 6zm0 2c-2.7 0-8 1.3-8 4v3h16v-3c0-2.7-5.3-4-8-4z"
      />
    </BaseNavIcon>
  );
}

/** Front Desk — headset / concierge console. */
export function FrontDeskIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M12 3a7 7 0 00-7 7v3.2c0 .99.81 1.8 1.8 1.8H8.2c.44 0 .8-.36.8-.8V12c0-.44-.36-.8-.8-.8H6.6V10a5.4 5.4 0 1110.8 0v1.2h-1.6c-.44 0-.8.36-.8.8v2.2c0 .44.36.8.8.8h1.4c.99 0 1.8-.81 1.8-1.8V10A7 7 0 0012 3zm-3.2 13.2c-.5 1.66.4 3.3 2 3.8.3.1.6.15.9.16V21h.6v-.84c1.76-.2 3.1-1.7 3.1-3.46h-1.2c0 1.21-.99 2.2-2.2 2.2s-2.2-.99-2.2-2.2H8.8z"
      />
    </BaseNavIcon>
  );
}

/** Amenities — calendar/booking icon */
export function AmenityIcon(props: SvgIconProps) {
  return (
    <BaseNavIcon {...props}>
      <path
        fill="currentColor"
        d="M17 2h-1V1h-2v1H10V1H8v1H7C5.9 2 5 2.9 5 4v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H7V7h10v13zM9 9h2v2H9V9zm4 0h2v2h-2V9zm-4 4h2v2H9v-2zm4 4h2v2h-2v-2zm-4 0h2v2H9v-2zm4-4h2v2h-2v-2z"
      />
    </BaseNavIcon>
  );
}
